import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Camera, Check, Loader2, Send, Sparkles } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/landing/Header";
import { apiGet, apiPost } from "@/lib/api";
import { BeforeAfterGallery } from "@/components/landing/BeforeAfter";

const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
const field = "w-full h-11 rounded-xl px-3 text-sm bg-white/[0.04] border border-white/10 text-white placeholder:text-gray-600 focus:border-sky-500 outline-none transition-colors";
const STEPS = ["Your info", "Photo", "AI quote", "Finish"];

const shrink = (file) => new Promise((res, rej) => {
  const img = new Image();
  img.onload = () => {
    const k = Math.min(1, 1600 / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = img.width * k; c.height = img.height * k;
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    res(c.toDataURL("image/jpeg", 0.85));
  };
  img.onerror = () => rej(new Error("That file isn't a photo we can read — try a JPG or PNG"));
  img.src = URL.createObjectURL(file);
});

const Options = ({ svc, sel, setSel }) => (svc.options || []).map((g) => (
  <div key={g.id} className="space-y-2" data-testid={`option-group-${g.id}`}>
    <p className="text-sm text-white">{g.label}{g.required && <span className="text-sky-400"> *</span>}{g.type === "multi" && <span className="text-gray-500 text-xs"> · pick any</span>}</p>
    <div className="flex flex-wrap gap-2">
      {g.choices.map((c) => {
        const on = (sel[g.id] || []).includes(c.label);
        const pick = () => setSel({ ...sel, [g.id]: g.type === "multi" ? (on ? sel[g.id].filter((x) => x !== c.label) : [...(sel[g.id] || []), c.label]) : [c.label] });
        return (
          <button key={c.label} type="button" onClick={pick} data-testid={`option-${g.id}-${c.label.replace(/\W+/g, "-").toLowerCase()}`}
            className={`px-4 py-2 rounded-full text-sm border transition-colors ${on ? "border-sky-400 bg-sky-500/15 text-white" : "border-white/10 text-gray-400 hover:text-white"}`}>
            {c.label}{c.price ? <span className="text-sky-300"> +{money(c.price)}</span> : null}
          </button>
        );
      })}
    </div>
  </div>
));

const InfoStep = ({ svc, info, setInfo, sel, setSel, next }) => {
  const set = (k) => (e) => setInfo({ ...info, [k]: e.target.value });
  const missing = (svc.options || []).find((g) => g.required && !(sel[g.id] || []).length);
  const ok = info.name.trim() && info.phone.replace(/\D/g, "").length >= 10 && !missing;
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-3">
        <input className={field} placeholder="Full name *" value={info.name} onChange={set("name")} data-testid="book-name" />
        <input className={field} placeholder="Phone *" inputMode="tel" value={info.phone} onChange={set("phone")} data-testid="book-phone" />
        <input className={field} placeholder="Email" value={info.email} onChange={set("email")} data-testid="book-email" />
        <input className={field} placeholder="Service address" value={info.address} onChange={set("address")} data-testid="book-address" />
        <input className={field} placeholder="Year" value={info.year} onChange={set("year")} data-testid="book-year" />
        <input className={field} placeholder="Make & model (e.g. Tesla Model Y)" value={info.model} onChange={set("model")} data-testid="book-model" />
      </div>
      <Options svc={svc} sel={sel} setSel={setSel} />
      <textarea className={`${field} h-auto py-2`} rows={2} placeholder="Anything we should know? (optional)" value={info.notes} onChange={set("notes")} data-testid="book-notes" />
      <button disabled={!ok} onClick={next} className="h-12 px-8 rounded-xl bg-sky-500 hover:bg-sky-400 disabled:opacity-40 text-white transition-colors" data-testid="book-next-info">Continue</button>
      {missing && <p className="text-xs text-amber-300">Choose {missing.label} to continue.</p>}
    </div>
  );
};

const PhotoStep = ({ photo, setPhoto, busy, submit }) => {
  const ref = useRef(null);
  const pick = (f) => f && shrink(f).then(setPhoto).catch((e) => toast.error(e.message));
  return (
    <div className="space-y-5">
      <button type="button" onClick={() => ref.current.click()} data-testid="book-photo-btn"
        className="relative w-full aspect-video rounded-2xl border border-dashed border-sky-500/40 hover:border-sky-400 overflow-hidden flex items-center justify-center text-gray-400 transition-colors">
        {photo ? <img src={photo} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <span className="flex flex-col items-center gap-2"><Camera className="w-7 h-7 text-sky-400" />Add 1 clear photo of the whole car</span>}
        <input ref={ref} type="file" accept="image/*" capture="environment" hidden onChange={(e) => pick(e.target.files[0])} data-testid="book-photo-input" />
      </button>
      <button disabled={!photo || busy} onClick={submit} className="h-12 px-8 rounded-xl bg-sky-500 hover:bg-sky-400 disabled:opacity-40 text-white flex items-center gap-2 transition-colors" data-testid="book-get-quote">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin" />AI is checking your car…</> : <><Sparkles className="w-4 h-4" />Get my AI quote</>}
      </button>
    </div>
  );
};

const QuoteStep = ({ q, mode, onDeposit, onRequest }) => (
  <div className="space-y-5" data-testid="book-quote">
    {q.ai_summary && <p className="text-sky-100/90 text-sm">{q.ai_summary}</p>}
    <div className="rounded-2xl border border-white/10 divide-y divide-white/5">
      {q.line_items.map((i) => <div key={i.name} className="flex justify-between px-4 py-2.5 text-sm"><span className="text-gray-400">{i.name}</span><span className="text-white">{money(i.price)}</span></div>)}
      <div className="flex justify-between px-4 py-3"><span className="text-white">Total</span><span className="text-2xl font-['Outfit'] text-white" data-testid="book-total">{money(q.total)}</span></div>
      {mode === "deposit" && <div className="flex justify-between px-4 py-3 text-sm"><span className="text-gray-400">Deposit to book ({q.deposit_pct}%, non-refundable)</span><span className="text-sky-300" data-testid="book-deposit">{money(q.deposit)}</span></div>}
    </div>
    <div className="flex flex-wrap gap-3">
      {mode === "deposit" && <button onClick={onDeposit} className="h-12 px-8 rounded-xl bg-sky-500 hover:bg-sky-400 text-white transition-colors" data-testid="book-pick-date">Pick a date & pay deposit</button>}
      <button onClick={onRequest} className={`h-12 px-6 rounded-xl border transition-colors ${mode === "deposit" ? "border-white/15 text-gray-300 hover:text-white" : "bg-sky-500 hover:bg-sky-400 border-sky-500 text-white"}`} data-testid="book-request">
        {mode === "deposit" ? "Have the owner review it first" : "Send my request"}
      </button>
    </div>
  </div>
);

export const DateStep = ({ q, navigate }) => {
  const tomorrow = new Date(Date.now() + 864e5).toLocaleDateString("en-CA", { timeZone: "America/Phoenix" });
  const [date, setDate] = useState(tomorrow);
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSlots(null); setSlot(""); apiGet("/slots", { date }).then((d) => setSlots(d.slots || [])).catch(() => setSlots([])); }, [date]);
  const book = () => { setBusy(true); apiPost(`/service-quote/${q.quote_id}/book`, { date, slot }).then((r) => navigate(r.pay_url)).catch((e) => { toast.error(e?.response?.data?.detail || "Booking failed"); setBusy(false); }); };
  const fmt = (t) => { const [h, m] = t.split(":").map(Number); return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };
  return (
    <div className="space-y-5" data-testid="book-date-step">
      <input type="date" min={tomorrow} value={date} onChange={(e) => setDate(e.target.value)} className={`${field} w-56`} data-testid="book-date" />
      {!slots ? <Loader2 className="w-5 h-5 animate-spin text-sky-500" /> : slots.length === 0 ? <p className="text-sm text-gray-500">No openings that day — try another date.</p> : (
        <div className="flex flex-wrap gap-2">{slots.map((t) => <button key={t} onClick={() => setSlot(t)} data-testid={`book-slot-${t}`} className={`px-4 py-2 rounded-full text-sm border transition-colors ${slot === t ? "border-sky-400 bg-sky-500/15 text-white" : "border-white/10 text-gray-400 hover:text-white"}`}>{fmt(t)}</button>)}</div>
      )}
      <button disabled={!slot || busy} onClick={book} className="h-12 px-8 rounded-xl bg-sky-500 hover:bg-sky-400 disabled:opacity-40 text-white flex items-center gap-2 transition-colors" data-testid="book-confirm">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}Continue to deposit {money(q.deposit)}
      </button>
    </div>
  );
};

const RequestStep = ({ q }) => {
  const [pref, setPref] = useState("");
  const [sent, setSent] = useState(false);
  const send = () => apiPost(`/service-quote/${q.quote_id}/request`, { preferred: pref }).then(() => setSent(true)).catch(() => toast.error("Couldn't send — please call us"));
  if (sent) return <div className="flex items-center gap-3 text-emerald-300" data-testid="book-request-sent"><Check className="w-6 h-6" />Request sent! We'll text you shortly to confirm your price and time.</div>;
  return (
    <div className="space-y-4" data-testid="book-request-step">
      <input className={field} placeholder="When works best? e.g. Saturday morning" value={pref} onChange={(e) => setPref(e.target.value)} data-testid="book-preferred" />
      <button onClick={send} className="h-12 px-8 rounded-xl bg-sky-500 hover:bg-sky-400 text-white flex items-center gap-2 transition-colors" data-testid="book-send-request"><Send className="w-4 h-4" />Send to Reflections</button>
    </div>
  );
};

export default function ServiceBookingPage() {
  const { key } = useParams();
  const navigate = useNavigate();
  const [svc, setSvc] = useState(null);
  const [step, setStep] = useState(0);
  const [finish, setFinish] = useState("deposit");
  const [info, setInfo] = useState({ name: "", phone: "", email: "", address: "", year: "", model: "", notes: "" });
  const [sel, setSel] = useState({});
  const [photo, setPhoto] = useState("");
  const [q, setQ] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { apiGet(`/services/${key}`).then(setSvc).catch(() => navigate("/services", { replace: true })); }, [key, navigate]);
  const quote = () => {
    setBusy(true);
    apiPost("/service-quote", { service_key: key, name: info.name, phone: info.phone, email: info.email, address: info.address, notes: info.notes,
      vehicle: { year: info.year, model: info.model }, selections: sel, photo_base64: photo })
      .then((d) => { setQ(d); setStep(2); }).catch((e) => toast.error(e?.response?.data?.detail || "Quote failed — try again")).finally(() => setBusy(false));
  };
  if (!svc) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-sky-500" /></div>;
  const mode = svc.booking_mode || "deposit";
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="service-booking-page">
      <Header onQuoteClick={() => navigate("/services")} />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-28 sm:pt-32 pb-20">
        <button onClick={() => (step ? setStep(step === 3 ? 2 : step - 1) : navigate("/services"))} className="flex items-center gap-2 text-gray-400 hover:text-sky-300 text-sm mb-6 transition-colors" data-testid="book-back"><ArrowLeft className="w-4 h-4" />Back</button>
        <p className="text-sky-500 text-xs tracking-[0.3em] uppercase mb-3">Book</p>
        <h1 className="font-['Outfit'] text-4xl sm:text-5xl text-white mb-3" data-testid="book-service-name">{svc.name}</h1>
        {svc.description && <p className="text-gray-500 mb-8 max-w-2xl">{svc.description}</p>}
        {step === 0 && <BeforeAfterGallery items={svc.gallery} />}
        <div className="flex gap-2 mb-8">{STEPS.map((s, i) => <div key={s} className="flex-1"><div className={`h-1 rounded-full transition-colors ${i <= step ? "bg-sky-500" : "bg-white/10"}`} /><p className={`text-[11px] mt-1.5 ${i === step ? "text-white" : "text-gray-600"}`}>{s}</p></div>)}</div>
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }}>
            {step === 0 && <InfoStep svc={svc} info={info} setInfo={setInfo} sel={sel} setSel={setSel} next={() => setStep(1)} />}
            {step === 1 && <PhotoStep photo={photo} setPhoto={setPhoto} busy={busy} submit={quote} />}
            {step === 2 && q && <QuoteStep q={q} mode={mode} onDeposit={() => { setFinish("deposit"); setStep(3); }} onRequest={() => { setFinish("request"); setStep(3); }} />}
            {step === 3 && q && (finish === "deposit" ? <DateStep q={q} navigate={navigate} /> : <RequestStep q={q} />)}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}
